(function(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.MDriveTIEngine = api;
})(typeof window !== 'undefined' ? window : globalThis, function() {
  'use strict';
  const round = n => Math.round(n / 1000) * 1000;
  const amount = (v, base) => typeof v === 'string' && /^\d+(\.\d+)?%$/.test(v) ? Math.round(base * parseFloat(v) / 100) : Number(v || 0);
  const day = d => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return null;
    const t = Date.parse(d + 'T00:00:00Z');
    return Number.isFinite(t) && new Date(t).toISOString().slice(0,10) === d ? Math.floor(t / 86400000) : null;
  };
  function today(now = new Date()) {
    return new Date(now.getTime() + 390 * 60000).toISOString().slice(0,10);
  }
  function bytesToBase64(bytes) {
    let binary = '';
    for (let i=0;i<bytes.length;i+=8192) binary += String.fromCharCode(...bytes.subarray(i,i+8192));
    return btoa(binary);
  }
  function base64ToBytes(value) {
    const binary = atob(value), bytes = new Uint8Array(binary.length);
    for (let i=0;i<binary.length;i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  function randomSalt(length=28) {
    const alphabet='ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$%&*!?';
    const bytes=new Uint8Array(length);
    if (typeof crypto!=='undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
    else for(let i=0;i<length;i++) bytes[i]=Math.floor(Math.random()*256);
    return Array.from(bytes,b=>alphabet[b%alphabet.length]).join('');
  }
  function encodeConfig(config,salt=randomSalt()) {
    const source=new TextEncoder().encode(JSON.stringify(config));
    const key=new TextEncoder().encode(salt);
    const encoded=new Uint8Array(source.length);
    for(let i=0;i<source.length;i++) encoded[i]=source[i]^key[i%key.length];
    return {version:2,encoding:'utf8-xor-base64',data:bytesToBase64(encoded),salt};
  }
  function decodeConfig(bundle) {
    if(!bundle || bundle.version!==2 || bundle.encoding!=='utf8-xor-base64' || !bundle.data || !bundle.salt) throw new Error('Invalid trade-in data bundle.');
    const source=base64ToBytes(bundle.data), key=new TextEncoder().encode(bundle.salt), decoded=new Uint8Array(source.length);
    for(let i=0;i<source.length;i++) decoded[i]=source[i]^key[i%key.length];
    return JSON.parse(new TextDecoder().decode(decoded));
  }
  function settings(data, model) {
    const o = model.settings || {};
    const get = k => o[k] == null || o[k] === 'inherit' ? data.defaults[k] : o[k];
    return { flow:get('flow'), display:get('display'), promotion:get('promotion'), bonusOverride:o.bonusOverride == null ? null : o.bonusOverride };
  }
  function estimate(model, input) {
    const tier = model.storages?.[input.storage]?.batteryTiers?.[input.battery];
    if (!tier || !Number.isFinite(tier.vl)) return { status:'manual', reason:'This battery level needs an in-store estimate.' };
    const base = tier.vl;
    const answers = input.answers || {};
    const rules = model.rules || {};
    if ((rules.rejectIfNo || []).some(q => answers[q] === 'no') || (rules.rejectCombos || []).some(c => c.length && c.every(q => answers[q] === 'no'))) return {status:'rejected',reason:'Device condition does not meet the trade-in requirements.'};
    const region = amount(model.regions?.[input.region]?.deduct, base);
    const accessories = (input.box === 'no' ? amount(model.accessories?.box,base) : 0) + (input.cable === 'no' ? amount(model.accessories?.cable,base) : 0);
    const deductions = model.storages[input.storage].deductions || model.deductions || {};
    const condition = answers.q1 === 'yes' ? 0 : Object.entries(answers).reduce((n,[q,a]) => n + (a === 'no' ? amount(deductions[q],base) : 0),0);
    const total = region + accessories + condition;
    const threshold = rules.autoRejectPct == null ? .8 : Number(rules.autoRejectPct);
    if (total >= base * threshold) return {status:'rejected',reason:'The total deductions exceed the accepted limit.',base,total};
    return {status:'ok',base,region,accessories,condition,value:round(Math.max(0,base-total)),provisional:!!model.policyPending};
  }
  function campaign(data, modelName, target, receipt = {}, now = new Date()) {
    const model = data.models[modelName];
    if (!model) return null;
    const s = settings(data,model);
    if (s.promotion === 'off' || s.display === 'normal') return null;
    const current = day(today(now));
    for (const c of data.campaigns || []) {
      if (!c.enabled || (c.fromModels.length && !c.fromModels.includes(modelName))) continue;
      if (target && c.toModels.length && !c.toModels.includes(target)) continue;
      const starts = day(c.start), ends = day(c.end);
      const within = current >= starts && current <= ends;
      let eligible = false, reason = '', conditional = !target;
      if (c.showEligibilitySwitcher === false) {
        eligible = within;
        reason = 'campaign';
      } else if (receipt.kind === 'purchased') {
        eligible = within && c.priorPurchaseDates.length > 0;
        reason = 'prior-purchase';
      } else if (receipt.kind === 'preorder') {
        eligible = current >= starts && (within || (c.allowAfterEnd && current > ends));
        reason = 'preorder-collection';
        conditional = true;
      }
      if (eligible) return {...c, amount:s.bonusOverride == null ? c.amount : s.bonusOverride, conditional, reason};
    }
    return null;
  }
  function result(data, modelName, input, target, retail, receipt, now) {
    const normal = estimate(data.models[modelName],input);
    if (normal.status !== 'ok') return normal;
    const promo = campaign(data,modelName,target,receipt,now);
    const bonus = promo ? round(promo.amount) : 0;
    const campaignValue = normal.value + bonus;
    return {...normal,promo,bonus,campaignValue,normalPayable:retail == null ? null : round(Math.max(0,retail-normal.value)),payable:retail == null ? null : round(Math.max(0,retail-campaignValue))};
  }
  function validateUnsafe(data) {
    const errors = [];
    const fail = (ok,msg) => {if(!ok) errors.push(msg);};
    const nonneg = x => typeof x === 'number' && Number.isFinite(x) && x >= 0;
    const deduction = x => nonneg(x) || (typeof x === 'string' && /^\d+(\.\d+)?%$/.test(x) && parseFloat(x) <= 100);
    if (!data || typeof data !== 'object') return ['Invalid configuration.'];
    fail(data.version === 2,'Configuration version must be 2.');
    fail(['upgrade','direct'].includes(data.defaults?.flow),'Choose a default result flow.');
    fail(['both','promotion','normal'].includes(data.defaults?.display),'Choose a default price display.');
    fail(['on','off'].includes(data.defaults?.promotion),'Choose a default promotion setting.');
    fail(Array.isArray(data.regions) && data.regions.length > 0,'Region list is required.');
    fail(data.models && Object.keys(data.models).length > 0,'At least one trade-in model is required.');
    for (const [name,m] of Object.entries(data.models || {})) {
      fail(typeof m === 'object' && m !== null,`${name}: invalid model.`); if(!m) continue;
      const s = m.settings || {};
      fail(['inherit','upgrade','direct',undefined].includes(s.flow),`${name}: invalid flow.`);
      fail(['inherit','both','promotion','normal',undefined].includes(s.display),`${name}: invalid price display.`);
      fail(['inherit','on','off',undefined].includes(s.promotion),`${name}: invalid promotion override.`);
      fail(s.bonusOverride == null || (nonneg(s.bonusOverride) && s.bonusOverride%1000===0),`${name}: bonus must be a nonnegative multiple of 1,000.`);
      fail(Object.keys(m.storages || {}).length > 0,`${name}: storage is missing.`);
      for (const [storage,v] of Object.entries(m.storages || {})) {
        fail(Object.keys(v.batteryTiers || {}).length > 0,`${name} ${storage}: battery tiers missing.`);
        for (const [tier,p] of Object.entries(v.batteryTiers || {})) fail(p.vl === null || (nonneg(p.vl) && p.vl > 0),`${name} ${storage} ${tier}: invalid value.`);
        for(const [q,d] of Object.entries(v.deductions || {})) fail(deduction(d),`${name} ${storage} ${q}: invalid deduction.`);
      }
      for(const [q,d] of Object.entries(m.deductions || {})) fail(deduction(d),`${name} ${q}: invalid deduction.`);
      for(const [r,d] of Object.entries(m.regions || {})) fail(deduction(d.deduct),`${name} ${r}: invalid deduction.`);
      for(const k of ['box','cable']) fail(deduction(m.accessories?.[k] ?? 0),`${name}: invalid ${k} deduction.`);
      fail(Number.isFinite(m.rules?.autoRejectPct) && m.rules.autoRejectPct > 0 && m.rules.autoRejectPct <= 1,`${name}: rejection threshold must be >0 and <=1.`);
      fail(Array.isArray(m.rules?.rejectIfNo) && m.rules.rejectIfNo.every(q=>/^q[2-9]$/.test(q)),`${name}: invalid immediate-rejection rules.`);
      fail(Array.isArray(m.rules?.rejectCombos) && m.rules.rejectCombos.every(c=>Array.isArray(c)&&c.length&&c.every(q=>/^q[2-9]$/.test(q))),`${name}: invalid rejection combinations.`);
    }
    for(const [name,d] of Object.entries(data.newDevices || {})) {
      fail(Object.keys(d.prices || {}).length > 0,`${name}: retail prices missing.`);
      for(const [storage,p] of Object.entries(d.prices || {})) fail(nonneg(p) && p > 0,`${name} ${storage}: invalid retail price.`);
      fail(Array.isArray(d.colors),`${name}: colours must be a list.`);
    }
    const ids = new Set();
    for(const c of data.campaigns || []) {
      fail(!!c.id && !ids.has(c.id),'Campaign IDs must be unique.'); ids.add(c.id);
      fail(day(c.start)!==null && day(c.end)!==null && day(c.start)<=day(c.end),`${c.name}: invalid date range.`);
      fail(nonneg(c.amount) && c.amount%1000===0,`${c.name}: bonus must be a nonnegative multiple of 1,000.`);
      fail(Array.isArray(c.fromModels) && Array.isArray(c.toModels),`${c.name}: model lists required.`);
      for(const n of c.fromModels || []) fail(!!data.models[n],`${c.name}: unknown trade-in model ${n}.`);
      for(const n of c.toModels || []) fail(!!data.newDevices[n],`${c.name}: unknown new device ${n}.`);
      fail(Array.isArray(c.priorPurchaseDates) && c.priorPurchaseDates.every(d=>day(d)!==null),`${c.name}: invalid prior purchase dates.`);
      fail(day(c.preorderDate)!==null,`${c.name}: invalid preorder date.`);
      fail(Number.isInteger(c.collectionDays) && c.collectionDays>=0,`${c.name}: collection days must be nonnegative.`);
      fail(typeof c.showEligibilitySwitcher==='boolean',`${c.name}: choose whether to show the eligibility switcher.`);
      fail(typeof c.allowAfterEnd==='boolean',`${c.name}: invalid post-campaign preorder setting.`);
    }
    return errors;
  }
  function validate(data) {
    try{return validateUnsafe(data);}catch(e){return ['Malformed configuration. Import a valid version 2 JSON backup or data file.'];}
  }
  return {round,amount,day,today,settings,estimate,campaign,result,validate,encodeConfig,decodeConfig};
});
